import { useState, useEffect, useMemo, useRef, useCallback } from "react";
import { Link, useNavigate, useLocation, useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import {
  WarningCircle,
  Plus,
  Trash,
  Eye,
  MagnifyingGlass,
  ArrowsClockwise,
  CaretLeft,
  CaretRight,
  CaretDown,
  DownloadSimple,
  FileCsv,
  X,
  IdentificationCard,
  User,
  CalendarBlank,
  MapPin,
  CurrencyInr,
  NotePencil,
  UserSwitch,
  Funnel,
  Clock,
  CheckCircle,
  FileText,
  UploadSimple,
  FileArrowUp,
  Check
} from "@phosphor-icons/react";

export const CONTRACT_TYPES = [
  { value: "warranty", label: "Warranty" },
  { value: "out_of_warranty", label: "Out of Warranty" },
  { value: "amc", label: "AMC" },
  { value: "out_of_amc", label: "Out of AMC" },
  { value: "on_call", label: "On Call" },
  { value: "register", label: "Register" },
  { value: "other", label: "Other" }
];

export const SERVICE_STATUS_LABELS = {
  active: "Active",
  completed: "Completed",
  expired: "Expired",
  renewed: "Renewed",
  declined: "Declined"
};

export const SERVICE_STAGE_LABELS = {
  start_fu1: "Start FU-1",
  start_fu2: "Start FU-2",
  start_fu3: "Start FU-3",
  exp_fu1: "Renewal FU-1",
  exp_fu2: "Renewal FU-2",
  exp_fu3: "Renewal FU-3",
  exp_fu4: "Renewal FU-4",
  exp_fu5: "Renewal FU-5",
  oncall_fu1: "On Call FU-1",
  oncall_fu2: "On Call FU-2",
  oncall_fu3: "On Call FU-3",
  fu1: "Out of Warranty FU-1",
  fu2: "Out of Warranty FU-2",
  fu3: "Out of Warranty FU-3",
  fu4: "Out of Warranty FU-4",
  fu5: "Out of Warranty FU-5",
  quotation_sent: "Quotation Sent",
  quotation: "Quotation Initial",
  qfu1: "Quote FU-1",
  qfu2: "Quote FU-2",
  qfu3: "Quote FU-3",
  qfu4: "Quote FU-4",
  qfu5: "Quote FU-5",
  payment_reminder: "Payment Reminder Sent",
  pfu1: "Payment FU-1",
  pfu2: "Payment FU-2",
  pfu3: "Payment FU-3",
  pfu4: "Payment FU-4",
  pfu5: "Payment FU-5"
};

export const SERVICE_FOLLOWUP_LABELS = {
  due_today: "Due Today",
  expiring_30_days: "Expiring in 30 Days",
  failed: "Failed Follow-ups",
  payment_pending: "Payment Reminder Active",
  quotation_active: "Quotation Flow Active"
};

export const SERVICE_SORT_OPTIONS = [
  { value: "created_at_desc", label: "Created: Newest First" },
  { value: "created_at_asc", label: "Created: Oldest First" },
  { value: "expiry_date_asc", label: "Expiry: Soonest First" },
  { value: "expiry_date_desc", label: "Expiry: Latest First" },
  { value: "start_date_desc", label: "Start Date: Newest First" },
  { value: "card_no_asc", label: "Card No (A-Z)" },
  { value: "customer_name_asc", label: "Customer Name (A-Z)" },
  { value: "amount_desc", label: "Amount: High to Low" },
  { value: "amount_asc", label: "Amount: Low to High" }
];

export const getContractTypeLabel = (type, dynamicTypes = []) => {
  if (dynamicTypes && dynamicTypes.length > 0) {
    const foundDyn = dynamicTypes.find(t => t.value === type || t.key === type);
    if (foundDyn) return foundDyn.label || foundDyn.name;
  }
  const found = CONTRACT_TYPES.find(t => t.value === type);
  return found ? found.label : (type || "—");
};

const SERVICE_FILTER_STORAGE_KEY = "leadpilot_service_filters";

function loadStoredServiceFilters() {
  try {
    const raw = sessionStorage.getItem(SERVICE_FILTER_STORAGE_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

function saveStoredServiceFilters(filters) {
  try {
    sessionStorage.setItem(SERVICE_FILTER_STORAGE_KEY, JSON.stringify(filters));
  } catch { }
}

function clearStoredServiceFilters() {
  try {
    sessionStorage.removeItem(SERVICE_FILTER_STORAGE_KEY);
  } catch { }
}

export default function ServiceCRM() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const isSp = user?.role === "salesperson";

  const isReturningFromDetail = useMemo(() => {
    if (location.state?.fromMenuNav || location.state?.resetFilters) {
      return false;
    }
    if (location.state?.fromServiceDetail) {
      return true;
    }
    const prevRoute = sessionStorage.getItem("leadpilot_prev_route") || "";
    return /^\/service-crm\/[^/]+$/.test(prevRoute);
  }, [location.state]);

  const initialFilters = useMemo(() => {
    if (isReturningFromDetail) {
      const stored = loadStoredServiceFilters();
      if (stored) return stored;
    }

    clearStoredServiceFilters();

    const urlType = searchParams.get("contract_type") || searchParams.get("type") || "";
    const urlStatus = searchParams.get("status") || "";
    const urlStage = searchParams.get("stage") || "";
    const urlAssigned = searchParams.get("assigned_to") || "";
    const urlFollowup = searchParams.get("followup_status") || (searchParams.get("due_today") === "true" ? "due_today" : "");
    const urlSort = searchParams.get("sort_by") || "created_at_desc";
    const urlQ = searchParams.get("q") || "";
    const urlPage = Number(searchParams.get("page")) || 1;
    const urlPageSize = Number(searchParams.get("page_size")) || 15;
    const hasAny = Boolean(urlType || urlStatus || urlStage || urlAssigned || urlFollowup || urlQ);

    return {
      filterType: urlType,
      filterStatus: urlStatus,
      filterStage: urlStage,
      filterAssignedTo: urlAssigned,
      filterFollowupStatus: urlFollowup,
      sortBy: urlSort,
      q: urlQ,
      page: urlPage,
      pageSize: urlPageSize,
      showFilters: hasAny
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const [contracts, setContracts] = useState([]);
  const [salespeople, setSalespeople] = useState([]);
  const [serviceTypes, setServiceTypes] = useState(CONTRACT_TYPES);
  const [filterType, setFilterType] = useState(initialFilters.filterType || "");
  const [filterStatus, setFilterStatus] = useState(initialFilters.filterStatus || "");
  const [filterStage, setFilterStage] = useState(initialFilters.filterStage || "");
  const [filterAssignedTo, setFilterAssignedTo] = useState(initialFilters.filterAssignedTo || "");
  const [filterFollowupStatus, setFilterFollowupStatus] = useState(initialFilters.filterFollowupStatus || "");
  const [sortBy, setSortBy] = useState(initialFilters.sortBy || "created_at_desc");
  const [q, setQ] = useState(initialFilters.q || "");
  const [showFilters, setShowFilters] = useState(initialFilters.showFilters || false);
  const [loading, setLoading] = useState(false);

  const [selectedContracts, setSelectedContracts] = useState({});
  const [showAdd, setShowAdd] = useState(false);
  const [showImportModal, setShowImportModal] = useState(false);
  const [importing, setImporting] = useState(false);
  const [importFile, setImportFile] = useState(null);
  const [importDefaultType, setImportDefaultType] = useState("auto");
  const [importResult, setImportResult] = useState(null);
  const fileInputRef = useRef(null);
  const [showExportMenu, setShowExportMenu] = useState(false);
  const [exporting, setExporting] = useState(false);
  const exportDropdownRef = useRef(null);

  // Pagination states
  const [page, setPage] = useState(initialFilters.page || 1);
  const [pageSize, setPageSize] = useState(initialFilters.pageSize || 15);

  const activeFilterCount = [
    filterStage,
    filterStatus,
    filterType,
    filterAssignedTo,
    filterFollowupStatus,
    sortBy !== "created_at_desc" ? sortBy : ""
  ].filter(Boolean).length;

  const initialContractState = {
    card_no: "",
    assigned_to: "",
    customer_name: "",
    customer_phone: "",
    customer_email: "",
    address: "",
    area: "",
    postcode: "",
    product_name: "",
    contract_type: "warranty",
    start_date: new Date().toISOString().split('T')[0],
    expiry_date: "",
    amount: "",
    lead_id: "",
    notes: ""
  };

  // New contract form state
  const [newContract, setNewContract] = useState(initialContractState);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (exportDropdownRef.current && !exportDropdownRef.current.contains(event.target)) {
        setShowExportMenu(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    api.get("/service-types")
      .then(r => {
        if (r.data && r.data.length > 0) {
          const list = r.data.filter(t => t.is_active !== false).map(t => ({
            value: t.key,
            label: t.name,
            code: t.code,
            default_validity_days: t.default_validity_days
          }));
          if (list.length > 0) setServiceTypes(list);
        }
      })
      .catch(() => { });
  }, []);

  useEffect(() => {
    if (user?.role !== "salesperson") {
      api.get("/users/salespeople?department=service")
        .then(r => setSalespeople(r.data || []))
        .catch(() => { });
    }
  }, [user]);

  // Sync state to sessionStorage
  useEffect(() => {
    saveStoredServiceFilters({
      filterType,
      filterStatus,
      filterStage,
      filterAssignedTo,
      filterFollowupStatus,
      sortBy,
      q,
      page,
      pageSize,
      showFilters
    });
  }, [filterType, filterStatus, filterStage, filterAssignedTo, filterFollowupStatus, sortBy, q, page, pageSize, showFilters]);

  // Sync state to URL search params
  useEffect(() => {
    const next = new URLSearchParams();
    if (filterType) next.set("contract_type", filterType);
    if (filterStatus) next.set("status", filterStatus);
    if (filterStage) next.set("stage", filterStage);
    if (filterAssignedTo) next.set("assigned_to", filterAssignedTo);
    if (filterFollowupStatus) next.set("followup_status", filterFollowupStatus);
    if (sortBy && sortBy !== "created_at_desc") next.set("sort_by", sortBy);
    if (q) next.set("q", q);
    if (page > 1) next.set("page", String(page));
    if (pageSize !== 15) next.set("page_size", String(pageSize));
    setSearchParams(next, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterType, filterStatus, filterStage, filterAssignedTo, filterFollowupStatus, sortBy, q, page, pageSize]);

  const clearAllFilters = () => {
    setQ("");
    setFilterStage("");
    setFilterStatus("");
    setFilterType("");
    setFilterAssignedTo("");
    setFilterFollowupStatus("");
    setSortBy("created_at_desc");
    setPage(1);
    clearStoredServiceFilters();
    setSearchParams({}, { replace: true });
  };

  const removeFilter = (key) => {
    if (key === "q") setQ("");
    if (key === "filterStage") setFilterStage("");
    if (key === "filterStatus") setFilterStatus("");
    if (key === "filterType") setFilterType("");
    if (key === "filterAssignedTo") setFilterAssignedTo("");
    if (key === "filterFollowupStatus") setFilterFollowupStatus("");
    if (key === "sortBy") setSortBy("created_at_desc");
  };

  const loadContracts = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (filterType) params.contract_type = filterType;
      if (filterStatus) params.status = filterStatus;
      if (filterStage) params.stage = filterStage;
      if (filterAssignedTo) params.assigned_to = filterAssignedTo;
      if (filterFollowupStatus) params.followup_status = filterFollowupStatus;
      if (q) params.q = q;

      if (sortBy === "created_at_desc") {
        params.sort_by = "created_at";
        params.sort_order = -1;
      } else if (sortBy === "created_at_asc") {
        params.sort_by = "created_at";
        params.sort_order = 1;
      } else if (sortBy === "expiry_date_asc") {
        params.sort_by = "expiry_date";
        params.sort_order = 1;
      } else if (sortBy === "expiry_date_desc") {
        params.sort_by = "expiry_date";
        params.sort_order = -1;
      } else if (sortBy === "start_date_desc") {
        params.sort_by = "start_date";
        params.sort_order = -1;
      } else if (sortBy === "card_no_asc") {
        params.sort_by = "card_no";
        params.sort_order = 1;
      } else if (sortBy === "customer_name_asc") {
        params.sort_by = "customer_name";
        params.sort_order = 1;
      } else if (sortBy === "amount_desc") {
        params.sort_by = "amount";
        params.sort_order = -1;
      } else if (sortBy === "amount_asc") {
        params.sort_by = "amount";
        params.sort_order = 1;
      }

      const res = await api.get("/contracts", { params });
      setContracts(res.data || []);
      setSelectedContracts({});
    } catch (e) {
      console.error("Failed to load contracts:", e);
      toast.error(e.response?.data?.detail || "Failed to load contracts");
    } finally {
      setLoading(false);
    }
  }, [filterType, filterStatus, filterStage, filterAssignedTo, filterFollowupStatus, q, sortBy]);

  useEffect(() => {
    loadContracts();
  }, [loadContracts]);

  useEffect(() => {
    setPage(1);
  }, [filterType, filterStatus, filterStage, filterAssignedTo, filterFollowupStatus, q, sortBy, pageSize]);

  const total = contracts.length;
  const totalPages = Math.ceil(total / pageSize) || 1;
  const startIdx = total === 0 ? 0 : (page - 1) * pageSize + 1;
  const endIdx = Math.min(page * pageSize, total);
  const paginatedContracts = useMemo(() => {
    return contracts.slice((page - 1) * pageSize, page * pageSize);
  }, [contracts, page, pageSize]);

  const toggleSelect = (id) => setSelectedContracts((s) => ({ ...s, [id]: !s[id] }));
  const toggleSelectAll = () => {
    const pageIds = paginatedContracts.map((c) => c.id);
    const allPageSelected = pageIds.length > 0 && pageIds.every((id) => selectedContracts[id]);
    if (allPageSelected) {
      setSelectedContracts(prev => {
        const next = { ...prev };
        pageIds.forEach(id => delete next[id]);
        return next;
      });
    } else {
      setSelectedContracts(prev => {
        const next = { ...prev };
        pageIds.forEach(id => { next[id] = true; });
        return next;
      });
    }
  };
  const selectedIds = Object.keys(selectedContracts).filter((k) => selectedContracts[k]);

  const handleDeleteSelected = async () => {
    if (selectedIds.length === 0) return;
    if (!window.confirm(`Delete ${selectedIds.length} contract(s)?`)) return;
    try {
      await api.post("/contracts/bulk-delete", { contract_ids: selectedIds });
      toast.success("Contracts deleted");
      loadContracts();
    } catch (e) {
      toast.error("Failed to delete contracts");
    }
  };

  const handleExport = async (typeMode = "current") => {
    setShowExportMenu(false);
    setExporting(true);
    const toastId = toast.loading("Preparing CSV export...");
    try {
      const params = new URLSearchParams();
      let label = "services";

      if (typeMode === "selected") {
        if (selectedIds.length === 0) {
          toast.dismiss(toastId);
          toast.error("No contracts selected");
          setExporting(false);
          return;
        }
        params.append("ids", selectedIds.join(","));
        label = `selected_${selectedIds.length}_services`;
      } else if (typeMode === "all") {
        label = "all_services";
      } else if (typeMode === "current") {
        if (filterType) params.append("contract_type", filterType);
        if (filterStatus) params.append("status", filterStatus);
        if (filterStage) params.append("stage", filterStage);
        if (filterAssignedTo) params.append("assigned_to", filterAssignedTo);
        if (filterFollowupStatus) params.append("followup_status", filterFollowupStatus);
        if (q) params.append("q", q);
        label = filterType ? `${filterType}_filtered` : "filtered_services";
      } else {
        params.append("contract_type", typeMode);
        label = `${typeMode}_services`;
      }

      if (sortBy === "created_at_desc") {
        params.append("sort_by", "created_at");
        params.append("sort_order", "-1");
      } else if (sortBy === "created_at_asc") {
        params.append("sort_by", "created_at");
        params.append("sort_order", "1");
      } else if (sortBy === "expiry_date_asc") {
        params.append("sort_by", "expiry_date");
        params.append("sort_order", "1");
      } else if (sortBy === "expiry_date_desc") {
        params.append("sort_by", "expiry_date");
        params.append("sort_order", "-1");
      } else if (sortBy === "start_date_desc") {
        params.append("sort_by", "start_date");
        params.append("sort_order", "-1");
      } else if (sortBy === "card_no_asc") {
        params.append("sort_by", "card_no");
        params.append("sort_order", "1");
      } else if (sortBy === "customer_name_asc") {
        params.append("sort_by", "customer_name");
        params.append("sort_order", "1");
      } else if (sortBy === "amount_desc") {
        params.append("sort_by", "amount");
        params.append("sort_order", "-1");
      } else if (sortBy === "amount_asc") {
        params.append("sort_by", "amount");
        params.append("sort_order", "1");
      }

      const response = await api.get(`/contracts/export?${params.toString()}`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${label}_export.csv`);
      document.body.appendChild(link);
      link.click();
      link.parentNode.removeChild(link);
      window.URL.revokeObjectURL(url);
      toast.success("CSV export downloaded successfully", { id: toastId });
    } catch (e) {
      toast.error(e.response?.data?.detail || "Export failed", { id: toastId });
    } finally {
      setExporting(false);
    }
  };

  const handleImportSubmit = async (e) => {
    if (e) e.preventDefault();
    if (!importFile) {
      toast.error("Please select a CSV file to import");
      return;
    }
    const fd = new FormData();
    fd.append("file", importFile);
    fd.append("default_type", importDefaultType);
    setImporting(true);
    const toastId = toast.loading("Processing and importing service CSV...");
    try {
      const res = await api.post("/contracts/import/csv", fd, {
        headers: { "Content-Type": "multipart/form-data" }
      });
      toast.success(res.data?.message || "Service contracts imported successfully!", { id: toastId });
      setImportResult(res.data);
      loadContracts();
      setImportFile(null);
      if (fileInputRef.current) fileInputRef.current.value = "";
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to import CSV", { id: toastId });
    } finally {
      setImporting(false);
    }
  };

  const handleDownloadTemplate = async () => {
    try {
      const response = await api.get("/contracts/import/template", { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", "service_contracts_import_template.csv");
      document.body.appendChild(link);
      link.click();
      link.parentNode.removeChild(link);
      window.URL.revokeObjectURL(url);
      toast.success("Import template downloaded");
    } catch (e) {
      toast.error("Failed to download template");
    }
  };

  const formatAmount = (amt) => {
    if (amt === null || amt === undefined || amt === "") return "—";
    const num = Number(amt);
    return isNaN(num) ? amt : `₹${num.toLocaleString('en-IN')}`;
  };

  return (
    <div data-testid="service-crm-page" className="space-y-4">
      {/* Page Header */}
      <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4">
        <div>
          <div className="text-[10px] font-bold uppercase tracking-[0.3em] text-[#6B7280] mb-2">After-Sales</div>
          <h1 className="font-heading font-black text-3xl sm:text-5xl tracking-tighter text-[#111827]">Service & Contracts</h1>
        </div>
        <div className="flex items-center gap-2 sm:gap-3">
          <button
            onClick={() => setShowAdd(true)}
            className="bg-[#002FA7] text-white px-4 sm:px-5 py-2.5 font-bold hover:bg-[#002280] transition-colors flex items-center gap-2 text-sm shadow-sm"
            data-testid="add-contract-button"
          >
            <Plus size={16} weight="bold" />
            <span className="hidden sm:inline">Add Contract</span>
            <span className="sm:hidden">New</span>
          </button>
        </div>
      </div>

      {/* Search, Filter Bar, and Actions */}
      <div className="flex flex-col sm:flex-row gap-2">
        {/* Search Input */}
        <div className="relative flex-1">
          <MagnifyingGlass size={16} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-[#6B7280]" weight="bold" />
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search by Card No, Client, Phone, Email, Area, Address, Product..."
            data-testid="service-search-input"
            className="w-full pl-10 pr-9 py-2.5 border border-[#D1D5DB] bg-white text-sm focus:ring-2 focus:ring-[#002FA7] focus:border-[#002FA7] focus:outline-none placeholder:text-[#9CA3AF]"
          />
          {q && (
            <button
              type="button"
              onClick={() => setQ("")}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-[#9CA3AF] hover:text-[#111827] p-1 rounded-sm hover:bg-gray-100 transition-colors"
              title="Clear search"
            >
              <X size={14} weight="bold" />
            </button>
          )}
        </div>

        {/* Action Controls */}
        <div className="flex items-center gap-2">
          {/* Filters Toggle Button */}
          <button
            type="button"
            onClick={() => setShowFilters(!showFilters)}
            className={`px-3.5 py-2.5 text-sm font-bold border transition-colors flex items-center gap-2 ${showFilters || activeFilterCount > 0
              ? "bg-blue-50 border-[#002FA7] text-[#002FA7]"
              : "bg-white border-[#D1D5DB] text-[#374151] hover:bg-[#F3F4F6]"
              }`}
            data-testid="service-filter-toggle-btn"
          >
            <Funnel size={16} weight={activeFilterCount > 0 ? "fill" : "bold"} />
            <span>Filters</span>
            {activeFilterCount > 0 && (
              <span className="bg-[#002FA7] text-white text-[11px] px-1.5 py-0.5 rounded-full font-black leading-none">
                {activeFilterCount}
              </span>
            )}
            <CaretDown
              size={13}
              weight="bold"
              className={`transition-transform duration-200 ${showFilters ? "rotate-180" : ""}`}
            />
          </button>

          {/* Refresh Button */}
          <button
            type="button"
            onClick={loadContracts}
            disabled={loading}
            className="px-3.5 py-2.5 text-sm font-bold border border-[#D1D5DB] bg-white text-[#374151] hover:bg-[#F3F4F6] disabled:opacity-50 flex items-center gap-2 transition-colors"
            data-testid="service-refresh-btn"
            title="Refresh contracts list"
          >
            <ArrowsClockwise size={16} weight="bold" className={loading ? "animate-spin text-[#002FA7]" : ""} />
            <span className="hidden md:inline">Refresh</span>
          </button>

          {/* Import CSV Button */}
          <button
            type="button"
            onClick={() => {
              setImportResult(null);
              setImportFile(null);
              setShowImportModal(true);
            }}
            className="border border-[#D1D5DB] bg-white text-[#374151] hover:bg-[#F9FAFB] px-3.5 py-2.5 font-bold transition-colors flex items-center gap-2 text-sm"
            data-testid="service-import-btn"
            title="Import Contracts / Services from CSV"
          >
            <UploadSimple size={16} weight="bold" className="text-[#002FA7]" />
            <span className="hidden sm:inline">Import CSV</span>
            <span className="sm:hidden">Import</span>
          </button>

          {/* Export Dropdown */}
          <div className="relative" ref={exportDropdownRef}>
            <button
              type="button"
              onClick={() => setShowExportMenu(!showExportMenu)}
              disabled={exporting}
              className="border border-[#D1D5DB] bg-white text-[#374151] hover:bg-[#F9FAFB] px-3.5 py-2.5 font-bold transition-colors flex items-center gap-2 text-sm"
              data-testid="service-export-btn"
              title="Export Services to CSV"
            >
              <DownloadSimple size={16} weight="bold" className={exporting ? "animate-bounce text-[#002FA7]" : "text-[#4B5563]"} />
              <span className="hidden sm:inline">Export CSV</span>
              <span className="sm:hidden">Export</span>
              <CaretDown size={13} weight="bold" className="text-[#6B7280]" />
            </button>

            {showExportMenu && (
              <div className="absolute right-0 mt-2 w-72 bg-white border border-[#D1D5DB] shadow-xl z-50 py-2 divide-y divide-gray-100 text-xs">
                <div className="p-2 space-y-1">
                  <div className="text-[10px] font-bold uppercase tracking-wider text-gray-600 px-2 py-1">Quick Export</div>
                  <button
                    type="button"
                    onClick={() => handleExport("current")}
                    className="w-full text-left px-3 py-2 hover:bg-blue-50 hover:text-[#002FA7] font-semibold text-gray-800 flex items-center justify-between group transition-colors"
                  >
                    <span>Current Filtered View</span>
                    <span className="text-[11px] text-gray-600 group-hover:text-[#002FA7]">({contracts.length})</span>
                  </button>
                  <button
                    type="button"
                    onClick={() => handleExport("all")}
                    className="w-full text-left px-3 py-2 hover:bg-blue-50 hover:text-[#002FA7] font-semibold text-gray-800 flex items-center justify-between group transition-colors"
                  >
                    <span>All Service Records</span>
                    <FileCsv size={14} weight="bold" className="text-gray-600 group-hover:text-[#002FA7]" />
                  </button>
                  {selectedIds.length > 0 && (
                    <button
                      type="button"
                      onClick={() => handleExport("selected")}
                      className="w-full text-left px-3 py-2 bg-blue-50/50 hover:bg-blue-100/70 font-bold text-[#002FA7] flex items-center justify-between transition-colors"
                    >
                      <span>Selected Contracts</span>
                      <span className="bg-[#002FA7] text-white px-1.5 py-0.5 text-[10px] rounded-sm font-black">{selectedIds.length}</span>
                    </button>
                  )}
                </div>

                <div className="p-2 space-y-1">
                  <div className="text-[10px] font-bold uppercase tracking-wider text-gray-600 px-2 py-1">Export By Service Type</div>
                  <div className="max-h-56 overflow-y-auto space-y-0.5">
                    {serviceTypes.map(st => {
                      const count = contracts.filter(c => c.contract_type === st.value).length;
                      return (
                        <button
                          key={st.value}
                          type="button"
                          onClick={() => handleExport(st.value)}
                          className="w-full text-left px-3 py-1.5 hover:bg-emerald-50 hover:text-emerald-800 text-gray-700 flex items-center justify-between transition-colors text-xs font-medium"
                        >
                          <span className="truncate">{st.label}</span>
                          <span className="text-[11px] text-gray-600 font-bold ml-2">
                            {count > 0 ? `(${count})` : "—"}
                          </span>
                        </button>
                      );
                    })}
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Active Filter Badges Strip */}
      {(activeFilterCount > 0 || q) && (
        <div className="flex flex-wrap items-center gap-2 p-2.5 bg-[#F9FAFB] border border-[#E5E7EB] text-xs">
          <span className="text-[10px] font-black uppercase tracking-wider text-[#6B7280] mr-1">
            Active:
          </span>

          {q && (
            <span className="inline-flex items-center gap-1.5 bg-white border border-[#D1D5DB] px-2.5 py-1 rounded-sm font-medium text-[#111827]">
              <span>Search: <strong>"{q}"</strong></span>
              <button type="button" onClick={() => removeFilter("q")} className="text-[#9CA3AF] hover:text-[#DC2626]">
                <X size={12} weight="bold" />
              </button>
            </span>
          )}

          {filterStage && (
            <span className="inline-flex items-center gap-1.5 bg-blue-50 border border-blue-200 px-2.5 py-1 rounded-sm font-medium text-[#002FA7]">
              <span>Stage: <strong>{SERVICE_STAGE_LABELS[filterStage] || filterStage}</strong></span>
              <button type="button" onClick={() => removeFilter("filterStage")} className="text-blue-400 hover:text-[#DC2626]">
                <X size={12} weight="bold" />
              </button>
            </span>
          )}

          {filterStatus && (
            <span className="inline-flex items-center gap-1.5 bg-emerald-50 border border-emerald-200 px-2.5 py-1 rounded-sm font-medium text-emerald-800">
              <span>State: <strong>{SERVICE_STATUS_LABELS[filterStatus] || filterStatus}</strong></span>
              <button type="button" onClick={() => removeFilter("filterStatus")} className="text-emerald-400 hover:text-[#DC2626]">
                <X size={12} weight="bold" />
              </button>
            </span>
          )}

          {filterType && (
            <span className="inline-flex items-center gap-1.5 bg-purple-50 border border-purple-200 px-2.5 py-1 rounded-sm font-medium text-[#7E22CE]">
              <span>Type: <strong>{getContractTypeLabel(filterType, serviceTypes)}</strong></span>
              <button type="button" onClick={() => removeFilter("filterType")} className="text-purple-400 hover:text-[#DC2626]">
                <X size={12} weight="bold" />
              </button>
            </span>
          )}

          {filterFollowupStatus && (
            <span className={`inline-flex items-center gap-1.5 border px-2.5 py-1 rounded-sm font-medium ${filterFollowupStatus === "failed" ? "bg-red-50 border-red-200 text-[#DC2626]" : "bg-amber-50 border-amber-200 text-[#B45309]"
              }`}>
              <span>Follow-up: <strong>{SERVICE_FOLLOWUP_LABELS[filterFollowupStatus] || filterFollowupStatus}</strong></span>
              <button type="button" onClick={() => removeFilter("filterFollowupStatus")} className="text-gray-400 hover:text-[#DC2626]">
                <X size={12} weight="bold" />
              </button>
            </span>
          )}

          {filterAssignedTo && (
            <span className="inline-flex items-center gap-1.5 bg-indigo-50 border border-indigo-200 px-2.5 py-1 rounded-sm font-medium text-[#4F46E5]">
              <span>Assigned: <strong>{salespeople.find(s => s.id === filterAssignedTo)?.name || 'Salesperson'}</strong></span>
              <button type="button" onClick={() => removeFilter("filterAssignedTo")} className="text-indigo-400 hover:text-[#DC2626]">
                <X size={12} weight="bold" />
              </button>
            </span>
          )}

          {sortBy && sortBy !== "created_at_desc" && (
            <span className="inline-flex items-center gap-1.5 bg-slate-100 border border-slate-300 px-2.5 py-1 rounded-sm font-medium text-slate-800">
              <span>Sort: <strong>{SERVICE_SORT_OPTIONS.find(s => s.value === sortBy)?.label || sortBy}</strong></span>
              <button type="button" onClick={() => removeFilter("sortBy")} className="text-slate-400 hover:text-[#DC2626]">
                <X size={12} weight="bold" />
              </button>
            </span>
          )}

          <button
            type="button"
            onClick={clearAllFilters}
            className="ml-auto text-xs font-bold text-[#DC2626] hover:text-[#991B1B] hover:underline flex items-center gap-1 px-2 py-0.5"
          >
            <Trash size={12} weight="bold" /> Clear all
          </button>
        </div>
      )}

      {/* Expandable Filter Panel */}
      {showFilters && (
        <div className="bg-white border border-[#111827] p-4 shadow-sm" data-testid="service-filter-panel">
          <div className="flex items-center justify-between pb-3 mb-4 border-b border-[#E5E7EB]">
            <div className="flex items-center gap-2">
              <Funnel size={16} weight="bold" className="text-[#002FA7]" />
              <span className="font-heading font-black text-sm tracking-tight text-[#111827]">
                Filter Service & Contracts
              </span>
              {activeFilterCount > 0 && (
                <span className="text-[11px] font-bold bg-[#002FA7] text-white px-1.5 py-0.2 rounded-full">
                  {activeFilterCount}
                </span>
              )}
            </div>
            <div className="flex items-center gap-3">
              {activeFilterCount > 0 && (
                <button
                  type="button"
                  onClick={clearAllFilters}
                  className="text-xs font-bold text-[#DC2626] hover:text-[#991B1B] hover:underline flex items-center gap-1"
                >
                  <Trash size={12} weight="bold" /> Reset all
                </button>
              )}
              <button
                type="button"
                onClick={() => setShowFilters(false)}
                className="text-xs font-bold text-[#6B7280] hover:text-[#111827]"
              >
                Hide
              </button>
            </div>
          </div>

          {/* Quick Preset Buttons */}
          <div className="flex items-center gap-2 flex-wrap mb-4 pb-3 border-b border-[#F3F4F6]">
            <span className="text-[11px] font-bold uppercase tracking-wider text-[#6B7280] mr-1">Quick Presets:</span>
            <button
              type="button"
              onClick={() => setFilterFollowupStatus(filterFollowupStatus === "due_today" ? "" : "due_today")}
              className={`text-xs px-2.5 py-1 rounded-sm font-medium border transition-colors flex items-center gap-1.5 ${filterFollowupStatus === "due_today"
                ? "bg-[#DC2626] text-white border-[#DC2626] font-bold"
                : "bg-[#F9FAFB] text-[#374151] border-[#E5E7EB] hover:bg-[#F3F4F6]"
                }`}
            >
              <Clock size={12} weight="bold" /> Due Today
            </button>
            <button
              type="button"
              onClick={() => setFilterFollowupStatus(filterFollowupStatus === "expiring_30_days" ? "" : "expiring_30_days")}
              className={`text-xs px-2.5 py-1 rounded-sm font-medium border transition-colors flex items-center gap-1.5 ${filterFollowupStatus === "expiring_30_days"
                ? "bg-[#002FA7] text-white border-[#002FA7] font-bold"
                : "bg-[#F9FAFB] text-[#374151] border-[#E5E7EB] hover:bg-[#F3F4F6]"
                }`}
            >
              <CalendarBlank size={12} weight="bold" /> Expiring in 30 Days
            </button>
            <button
              type="button"
              onClick={() => setFilterFollowupStatus(filterFollowupStatus === "failed" ? "" : "failed")}
              className={`text-xs px-2.5 py-1 rounded-sm font-medium border transition-colors flex items-center gap-1.5 ${filterFollowupStatus === "failed"
                ? "bg-[#DC2626] text-white border-[#DC2626] font-bold"
                : "bg-[#F9FAFB] text-[#374151] border-[#E5E7EB] hover:bg-[#F3F4F6]"
                }`}
            >
              ⚠️ Failed Follow-ups
            </button>
            <button
              type="button"
              onClick={() => setFilterStage(filterStage === "payment_reminder" ? "" : "payment_reminder")}
              className={`text-xs px-2.5 py-1 rounded-sm font-medium border transition-colors flex items-center gap-1.5 ${filterStage === "payment_reminder"
                ? "bg-emerald-600 text-white border-emerald-600 font-bold"
                : "bg-[#F9FAFB] text-[#374151] border-[#E5E7EB] hover:bg-[#F3F4F6]"
                }`}
            >
              💳 Payment Reminders
            </button>
            <button
              type="button"
              onClick={() => setFilterStage(filterStage === "quotation_sent" ? "" : "quotation_sent")}
              className={`text-xs px-2.5 py-1 rounded-sm font-medium border transition-colors flex items-center gap-1.5 ${filterStage === "quotation_sent"
                ? "bg-[#7C3AED] text-white border-[#7C3AED] font-bold"
                : "bg-[#F9FAFB] text-[#374151] border-[#E5E7EB] hover:bg-[#F3F4F6]"
                }`}
            >
              📄 Quotes Sent
            </button>
            <button
              type="button"
              onClick={() => setFilterStatus(filterStatus === "active" ? "" : "active")}
              className={`text-xs px-2.5 py-1 rounded-sm font-medium border transition-colors flex items-center gap-1.5 ${filterStatus === "active"
                ? "bg-[#002FA7] text-white border-[#002FA7] font-bold"
                : "bg-[#F9FAFB] text-[#374151] border-[#E5E7EB] hover:bg-[#F3F4F6]"
                }`}
            >
              <CheckCircle size={12} weight="bold" /> Active Contracts
            </button>
          </div>

          {/* Filter Grid */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-3">
            {/* 1. Service Stage */}
            <div>
              <label className="block text-[11px] font-bold uppercase tracking-wider text-[#4B5563] mb-1">
                Service Stage
              </label>
              <select
                value={filterStage}
                onChange={(e) => setFilterStage(e.target.value)}
                data-testid="service-filter-stage"
                className={`w-full border px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-[#002FA7] ${filterStage ? "border-[#002FA7] font-bold text-[#002FA7] bg-blue-50/50" : "border-[#D1D5DB]"
                  }`}
              >
                <option value="">All Service Stages</option>
                <optgroup label="After Start Check-ins (Warranty/AMC)">
                  <option value="start_fu1">Start FU-1</option>
                  <option value="start_fu2">Start FU-2</option>
                  <option value="start_fu3">Start FU-3</option>
                </optgroup>
                <optgroup label="Renewal Reminders (Warranty/AMC)">
                  <option value="exp_fu1">Renewal FU-1</option>
                  <option value="exp_fu2">Renewal FU-2</option>
                  <option value="exp_fu3">Renewal FU-3</option>
                  <option value="exp_fu4">Renewal FU-4</option>
                  <option value="exp_fu5">Renewal FU-5</option>
                </optgroup>
                <optgroup label="On Call Follow-ups">
                  <option value="oncall_fu1">On Call FU-1</option>
                  <option value="oncall_fu2">On Call FU-2</option>
                  <option value="oncall_fu3">On Call FU-3</option>
                </optgroup>
                <optgroup label="Out of Coverage Follow-ups">
                  <option value="fu1">Out of Warranty FU-1</option>
                  <option value="fu2">Out of Warranty FU-2</option>
                  <option value="fu3">Out of Warranty FU-3</option>
                  <option value="fu4">Out of Warranty FU-4</option>
                  <option value="fu5">Out of Warranty FU-5</option>
                </optgroup>
                <optgroup label="Quotation Stages">
                  <option value="quotation_sent">Quotation Sent</option>
                  <option value="qfu1">Quote FU-1</option>
                  <option value="qfu2">Quote FU-2</option>
                  <option value="qfu3">Quote FU-3</option>
                  <option value="qfu4">Quote FU-4</option>
                  <option value="qfu5">Quote FU-5</option>
                </optgroup>
                <optgroup label="Payment Reminder Stages">
                  <option value="payment_reminder">Payment Reminder Sent</option>
                  <option value="pfu1">Payment FU-1</option>
                  <option value="pfu2">Payment FU-2</option>
                  <option value="pfu3">Payment FU-3</option>
                  <option value="pfu4">Payment FU-4</option>
                  <option value="pfu5">Payment FU-5</option>
                </optgroup>
              </select>
            </div>

            {/* 2. Contract State / Status */}
            <div>
              <label className="block text-[11px] font-bold uppercase tracking-wider text-[#4B5563] mb-1">
                Contract State
              </label>
              <select
                value={filterStatus}
                onChange={(e) => setFilterStatus(e.target.value)}
                data-testid="service-filter-status"
                className={`w-full border px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-[#002FA7] ${filterStatus ? "border-emerald-600 font-bold text-emerald-800 bg-emerald-50/50" : "border-[#D1D5DB]"
                  }`}
              >
                <option value="">All States / Statuses</option>
                <option value="active">Active</option>
                <option value="completed">Completed</option>
                <option value="expired">Expired</option>
                <option value="renewed">Renewed</option>
                <option value="declined">Declined</option>
              </select>
            </div>

            {/* 3. Service Type */}
            <div>
              <label className="block text-[11px] font-bold uppercase tracking-wider text-[#4B5563] mb-1">
                Service Type
              </label>
              <select
                value={filterType}
                onChange={(e) => setFilterType(e.target.value)}
                data-testid="service-filter-type"
                className={`w-full border px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#002FA7] ${filterType ? "border-[#7E22CE] text-[#7E22CE] font-bold bg-purple-50/50" : "border-[#D1D5DB] bg-white text-[#374151]"
                  }`}
              >
                <option value="">All Service Types</option>
                {serviceTypes.map((t) => (
                  <option key={t.value} value={t.value}>
                    {t.label}
                  </option>
                ))}
              </select>
            </div>

            {/* 4. Follow-up State */}
            <div>
              <label className="block text-[11px] font-bold uppercase tracking-wider text-[#4B5563] mb-1">
                Follow-up State
              </label>
              <select
                value={filterFollowupStatus}
                onChange={(e) => setFilterFollowupStatus(e.target.value)}
                data-testid="service-filter-followup"
                className={`w-full border px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#002FA7] ${filterFollowupStatus === "failed" || filterFollowupStatus === "due_today"
                  ? "border-[#EF4444] text-[#DC2626] font-bold bg-red-50"
                  : filterFollowupStatus
                    ? "border-[#002FA7] text-[#002FA7] font-bold bg-blue-50"
                    : "border-[#D1D5DB] bg-white text-[#374151]"
                  }`}
              >
                <option value="">All Follow-up States</option>
                <option value="due_today">⏰ Due Today</option>
                <option value="expiring_30_days">📅 Expiring Soon (30 Days)</option>
                <option value="failed">⚠️ Failed Follow-ups</option>
                <option value="payment_pending">💳 Payment Reminder Active</option>
                <option value="quotation_active">📄 Quotation Flow Active</option>
              </select>
            </div>

            {/* 5. Salesperson (admin / manager only) */}
            {!isSp && (
              <div>
                <label className="block text-[11px] font-bold uppercase tracking-wider text-[#4B5563] mb-1">
                  Salesperson
                </label>
                <select
                  value={filterAssignedTo}
                  onChange={(e) => setFilterAssignedTo(e.target.value)}
                  data-testid="service-filter-assigned"
                  className={`w-full border px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#002FA7] ${filterAssignedTo ? "border-[#4F46E5] text-[#4F46E5] font-bold bg-indigo-50/50" : "border-[#D1D5DB] bg-white text-[#374151]"
                    }`}
                >
                  <option value="">All Salespersons</option>
                  {salespeople.map((s) => (
                    <option key={s.id} value={s.id}>
                      {s.name}
                    </option>
                  ))}
                </select>
              </div>
            )}

            {/* 6. Sorting */}
            <div>
              <label className="block text-[11px] font-bold uppercase tracking-wider text-[#4B5563] mb-1">
                Sort Records
              </label>
              <select
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
                data-testid="service-filter-sort"
                className={`w-full border px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#002FA7] ${sortBy !== "created_at_desc" ? "border-slate-700 font-bold bg-slate-50" : "border-[#D1D5DB] bg-white text-[#374151]"
                  }`}
              >
                {SERVICE_SORT_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>
      )}

      {/* Multi-select Actions Strip */}
      {selectedIds.length > 0 && (
        <div className="flex flex-wrap items-center justify-between gap-3 p-3 bg-blue-50/70 border border-blue-200">
          <div className="flex items-center gap-2 text-xs font-bold text-[#002FA7]">
            <span>{selectedIds.length} contract(s) selected</span>
          </div>
          <div className="flex items-center gap-2 ml-auto">
            {user?.role !== "salesperson" && salespeople.length > 0 && (
              <select
                onChange={async (e) => {
                  const sp_id = e.target.value;
                  if (!sp_id) return;
                  try {
                    await api.post("/contracts/bulk-assign", {
                      contract_ids: selectedIds,
                      salesperson_id: sp_id
                    });
                    toast.success(`Reassigned ${selectedIds.length} contract(s)`);
                    loadContracts();
                  } catch (err) {
                    toast.error("Failed to reassign contracts");
                  }
                  e.target.value = "";
                }}
                defaultValue=""
                className="border border-[#002FA7] text-[#002FA7] font-bold px-3 py-1.5 text-xs bg-white hover:bg-blue-50 outline-none cursor-pointer"
              >
                <option value="" disabled>Bulk Assign ({selectedIds.length}) to...</option>
                {salespeople.map(s => (
                  <option key={s.id} value={s.id}>{s.name}</option>
                ))}
              </select>
            )}
            <button
              onClick={handleDeleteSelected}
              className="border border-[#EF4444] bg-white text-[#EF4444] font-bold px-3 py-1.5 text-xs hover:bg-[#EF4444] hover:text-white transition-colors flex items-center gap-1.5"
            >
              <Trash size={14} weight="bold" /> Delete Selected ({selectedIds.length})
            </button>
          </div>
        </div>
      )}

      {/* Data Table */}
      <div className="border border-[#E5E7EB] bg-white overflow-x-auto shadow-sm">
        {contracts.length === 0 ? (
          <div className="p-12 text-center text-sm text-[#6B7280]">No service contracts found matching your filter criteria.</div>
        ) : (
          <div className="min-w-full overflow-x-auto">
            <table className="w-full text-left border-collapse">
              <thead>
                <tr className="border-b-2 border-[#111827] bg-[#F9FAFB]">
                  <th className="py-3 px-3 w-8 text-center" onClick={e => e.stopPropagation()}>
                    <input type="checkbox" onChange={toggleSelectAll} checked={paginatedContracts.length > 0 && paginatedContracts.every(c => !!selectedContracts[c.id])} />
                  </th>
                  <th className="py-3 px-3 text-[10px] uppercase tracking-widest font-bold text-[#111827] whitespace-nowrap">Card No</th>
                  <th className="py-3 px-4 text-[10px] uppercase tracking-widest font-bold text-[#111827] whitespace-nowrap">Client / Customer</th>
                  <th className="py-3 px-3 text-[10px] uppercase tracking-widest font-bold text-[#111827] whitespace-nowrap">Phone / Email</th>
                  <th className="py-3 px-3 text-[10px] uppercase tracking-widest font-bold text-[#111827] whitespace-nowrap">Product / Amount</th>
                  <th className="py-3 px-3 text-[10px] uppercase tracking-widest font-bold text-[#111827] whitespace-nowrap">Start / Expiry</th>
                  <th className="py-3 px-3 text-[10px] uppercase tracking-widest font-bold text-[#111827] whitespace-nowrap">Type</th>
                  {user?.role !== "salesperson" && (
                    <th className="py-3 px-3 text-[10px] uppercase tracking-widest font-bold text-[#111827] whitespace-nowrap">Assigned</th>
                  )}
                  <th className="py-3 px-4 text-[10px] uppercase tracking-widest font-bold text-[#111827] whitespace-nowrap">Status</th>
                </tr>
              </thead>
              <tbody>
                {paginatedContracts.map(c => {
                  const assignedSp = salespeople.find(s => s.id === c.assigned_to);
                  return (
                    <tr
                      key={c.id}
                      onClick={() => navigate(`/service-crm/${c.id}`)}
                      className={`border-b border-[#E5E7EB] hover:bg-blue-50/40 cursor-pointer transition-colors ${selectedContracts[c.id] ? "bg-blue-50/70" : ""}`}
                    >
                      <td className="py-3 px-3 text-center" onClick={e => e.stopPropagation()}>
                        <input type="checkbox" checked={!!selectedContracts[c.id]} onChange={() => toggleSelect(c.id)} />
                      </td>
                      <td className="py-3 px-3 text-xs font-mono font-bold text-gray-800 whitespace-nowrap">
                        {c.card_no || "—"}
                      </td>
                      <td className="py-3 px-4">
                        <span className="font-bold text-sm text-[#002FA7] hover:underline block leading-tight">
                          {c.customer_name || (c.lead_id ? `Lead: ${c.lead_id.slice(0, 6)}` : "Unknown")}
                        </span>
                      </td>
                      <td className="py-3 px-3 text-xs">
                        <div className="font-mono text-gray-800 font-semibold whitespace-nowrap">{c.customer_phone || "—"}</div>
                        {c.customer_email && (
                          <div className="text-[11px] text-gray-500 truncate max-w-[180px]" title={c.customer_email}>{c.customer_email}</div>
                        )}
                      </td>
                      <td className="py-3 px-3 text-xs">
                        <div className="font-medium text-gray-900 whitespace-nowrap">{c.product_name || "—"}</div>
                        {c.amount !== null && c.amount !== undefined && c.amount !== "" && (
                          <div className="text-[11px] font-mono font-bold text-[#002FA7] whitespace-nowrap">{formatAmount(c.amount)}</div>
                        )}
                      </td>
                      <td className="py-3 px-3 text-xs font-mono whitespace-nowrap">
                        <div className="text-gray-700">
                          <span className="text-[10px] text-gray-400 font-sans font-bold uppercase mr-1">Start:</span>
                          {c.start_date ? new Date(c.start_date).toLocaleDateString('en-IN') : "—"}
                        </div>
                        <div className="text-gray-700">
                          <span className="text-[10px] text-gray-400 font-sans font-bold uppercase mr-1">Exp:</span>
                          {c.expiry_date ? new Date(c.expiry_date).toLocaleDateString('en-IN') : "—"}
                        </div>
                        {c.created_at && (
                          <div className="text-[10px] text-gray-400 font-sans font-semibold mt-0.5 whitespace-nowrap">
                            <span className="text-gray-400 font-bold uppercase mr-1 text-[9px]">Created:</span>
                            {new Date(c.created_at).toLocaleDateString('en-IN')}
                          </div>
                        )}
                      </td>
                      <td className="py-3 px-3 text-xs whitespace-nowrap">
                        <span className={`px-2 py-0.5 text-[11px] font-bold uppercase tracking-wider border ${c.contract_type?.includes('amc') ? 'bg-purple-50 text-purple-700 border-purple-200' :
                          c.contract_type?.includes('warranty') ? 'bg-blue-50 text-blue-700 border-blue-200' :
                            'bg-amber-50 text-amber-700 border-amber-200'
                          }`}>
                          {getContractTypeLabel(c.contract_type, serviceTypes)}
                        </span>
                      </td>
                      {user?.role !== "salesperson" && (
                        <td className="py-3 px-3 text-xs whitespace-nowrap" onClick={e => e.stopPropagation()}>
                          {user?.role === "admin" || user?.role === "manager" ? (
                            <select
                              value={c.assigned_to || ""}
                              onChange={async (e) => {
                                const sp_id = e.target.value;
                                if (!sp_id) return;
                                try {
                                  await api.post(`/contracts/${c.id}/assign`, { salesperson_id: sp_id });
                                  toast.success("Reassigned successfully");
                                  loadContracts();
                                } catch (err) {
                                  toast.error("Failed to reassign");
                                }
                              }}
                              className="border border-[#E5E7EB] px-2 py-1 text-xs bg-slate-50 hover:bg-white rounded font-medium text-slate-800 outline-none focus:border-[#002FA7]"
                            >
                              <option value="" disabled>Unassigned</option>
                              {salespeople.map(s => (
                                <option key={s.id} value={s.id}>{s.name}</option>
                              ))}
                            </select>
                          ) : (
                            <span className="inline-flex items-center px-2 py-0.5 text-xs font-medium bg-slate-100 text-slate-700 rounded">
                              {assignedSp?.name || "Unassigned"}
                            </span>
                          )}
                        </td>
                      )}
                      <td className="py-3 px-4 whitespace-nowrap">
                        <span className={`text-[10px] font-bold uppercase tracking-widest px-2 py-0.5 border ${c.status === 'active' ? 'text-[#10B981] border-[#10B981] bg-green-50' :
                          c.status === 'completed' ? 'text-[#7C3AED] border-[#7C3AED] bg-purple-50' :
                            c.status === 'expired' ? 'text-[#EF4444] border-[#EF4444] bg-red-50' :
                              c.status === 'renewed' ? 'text-[#002FA7] border-[#002FA7] bg-blue-50' :
                                'text-gray-700 border-gray-300 bg-gray-50'
                          }`}>
                          {c.status}
                        </span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Pagination Controls */}
      {total > 0 && (
        <div className="flex flex-col sm:flex-row items-center justify-between gap-3 bg-white border border-[#E5E7EB] p-3 sm:p-4 shadow-sm" data-testid="service-pagination">
          <div className="flex flex-wrap items-center gap-3 text-xs text-[#6B7280]">
            <div>
              Showing <b className="text-[#111827]">{startIdx}-{endIdx}</b> of <b className="text-[#111827]">{total}</b> records
            </div>
            <div className="flex items-center gap-1.5 border-l border-[#E5E7EB] pl-3">
              <span className="text-[#6B7280] font-semibold">Per page:</span>
              <select
                value={pageSize}
                onChange={e => setPageSize(Number(e.target.value))}
                className="border border-[#D1D5DB] px-2 py-1 text-xs font-bold bg-white text-[#374151] focus:outline-none focus:ring-1 focus:ring-[#002FA7]"
              >
                <option value={10}>10</option>
                <option value={15}>15</option>
                <option value={25}>25</option>
                <option value={50}>50</option>
                <option value={100}>100</option>
              </select>
            </div>
          </div>

          <div className="flex items-center gap-1">
            <button
              onClick={() => setPage(1)}
              disabled={page === 1}
              data-testid="page-first"
              title="First Page"
              className="border border-[#D1D5DB] px-2.5 py-1.5 text-xs font-bold hover:bg-[#F3F4F6] disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
            >
              «
            </button>
            <button
              onClick={() => setPage(p => Math.max(1, p - 1))}
              disabled={page === 1}
              data-testid="page-prev"
              className="border border-[#D1D5DB] px-3 py-1.5 text-xs font-bold hover:bg-[#F3F4F6] disabled:opacity-40 disabled:cursor-not-allowed flex items-center gap-1 transition-colors"
            >
              <CaretLeft size={12} weight="bold" /> Prev
            </button>
            <div className="border border-[#111827] bg-[#111827] text-white px-3 py-1.5 text-xs font-bold font-mono">
              {page} / {totalPages}
            </div>
            <button
              onClick={() => setPage(p => Math.min(totalPages, p + 1))}
              disabled={page === totalPages}
              data-testid="page-next"
              className="border border-[#D1D5DB] px-3 py-1.5 text-xs font-bold hover:bg-[#F3F4F6] disabled:opacity-40 disabled:cursor-not-allowed flex items-center gap-1 transition-colors"
            >
              Next <CaretRight size={12} weight="bold" />
            </button>
            <button
              onClick={() => setPage(totalPages)}
              disabled={page === totalPages}
              data-testid="page-last"
              title="Last Page"
              className="border border-[#D1D5DB] px-2.5 py-1.5 text-xs font-bold hover:bg-[#F3F4F6] disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
            >
              »
            </button>
          </div>
        </div>
      )}

      {/* Add Contract Modal with All Columns */}
      {showAdd && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-xs z-50 flex items-center justify-center p-3 sm:p-6 animate-fadeIn">
          <div className="bg-white w-full max-w-3xl shadow-2xl border border-[#D1D5DB] max-h-[92vh] flex flex-col overflow-hidden">
            {/* Modal Header */}
            <div className="flex justify-between items-center px-6 py-4 border-b border-[#E5E7EB] bg-gradient-to-r from-slate-50 to-white">
              <div>
                <span className="text-[10px] font-bold tracking-widest uppercase text-[#002FA7]">New Record</span>
                <h3 className="font-heading font-black text-xl sm:text-2xl text-[#111827] tracking-tight">Add Service Contract</h3>
              </div>
              <button
                onClick={() => {
                  setShowAdd(false);
                  setNewContract(initialContractState);
                }}
                className="text-[#6B7280] hover:text-[#111827] p-2 hover:bg-slate-100 transition-colors"
                title="Close"
              >
                <X size={20} weight="bold" />
              </button>
            </div>

            {/* Modal Body */}
            <div className="p-6 sm:p-7 space-y-4 overflow-y-auto flex-1 bg-white">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="text-xs font-semibold text-slate-700 block mb-1.5">
                    Card No <span className="text-red-500 font-bold">*</span>
                  </label>
                  <input
                    required
                    placeholder="e.g. 1-AMC or 1920-25-26"
                    className="w-full border border-slate-300 px-3.5 py-2.5 text-sm bg-slate-50/40 hover:bg-white focus:bg-white focus:ring-2 focus:ring-[#002FA7]/20 focus:border-[#002FA7] outline-none transition-all placeholder:text-slate-400"
                    value={newContract.card_no}
                    onChange={e => setNewContract({ ...newContract, card_no: e.target.value })}
                  />
                </div>
                <div>
                  <label className="text-xs font-semibold text-slate-700 block mb-1.5">
                    Service Type <span className="text-red-500 font-bold">*</span>
                  </label>
                  <select
                    className="w-full border border-slate-300 px-3.5 py-2.5 text-sm bg-slate-50/40 hover:bg-white focus:bg-white focus:ring-2 focus:ring-[#002FA7]/20 focus:border-[#002FA7] outline-none transition-all font-medium"
                    value={newContract.contract_type}
                    onChange={e => setNewContract({ ...newContract, contract_type: e.target.value })}
                  >
                    {serviceTypes.map(t => (
                      <option key={t.value} value={t.value}>{t.label}</option>
                    ))}
                  </select>
                </div>
              </div>

              {user?.role !== "salesperson" && salespeople.length > 0 && (
                <div>
                  <label className="text-xs font-semibold text-slate-700 block mb-1.5">
                    Assign to Salesperson (Optional)
                  </label>
                  <select
                    className="w-full border border-slate-300 px-3.5 py-2.5 text-sm bg-slate-50/40 hover:bg-white focus:bg-white focus:ring-2 focus:ring-[#002FA7]/20 focus:border-[#002FA7] outline-none transition-all font-medium"
                    value={newContract.assigned_to || ""}
                    onChange={e => setNewContract({ ...newContract, assigned_to: e.target.value })}
                  >
                    <option value="">Auto (Least loaded salesperson)</option>
                    {salespeople.map(s => (
                      <option key={s.id} value={s.id}>{s.name}</option>
                    ))}
                  </select>
                </div>
              )}

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="text-xs font-semibold text-slate-700 block mb-1.5">
                    Customer / Client Name <span className="text-red-500 font-bold">*</span>
                  </label>
                  <input
                    required
                    placeholder="e.g. Rajesh Shah"
                    className="w-full border border-slate-300 px-3.5 py-2.5 text-sm bg-slate-50/40 hover:bg-white focus:bg-white focus:ring-2 focus:ring-[#002FA7]/20 focus:border-[#002FA7] outline-none transition-all placeholder:text-slate-400"
                    value={newContract.customer_name}
                    onChange={e => setNewContract({ ...newContract, customer_name: e.target.value })}
                  />
                </div>
                <div>
                  <label className="text-xs font-semibold text-slate-700 block mb-1.5">
                    WhatsApp / Phone No <span className="text-red-500 font-bold">*</span>
                  </label>
                  <input
                    required
                    type="tel"
                    placeholder="10-digit mobile (e.g. 9876543210)"
                    className="w-full border border-slate-300 px-3.5 py-2.5 text-sm bg-slate-50/40 hover:bg-white focus:bg-white focus:ring-2 focus:ring-[#002FA7]/20 focus:border-[#002FA7] outline-none transition-all placeholder:text-slate-400"
                    value={newContract.customer_phone}
                    onChange={e => setNewContract({ ...newContract, customer_phone: e.target.value })}
                  />
                </div>
              </div>

              <div>
                <label className="text-xs font-semibold text-slate-700 block mb-1.5">
                  Email Address (Optional)
                </label>
                <input
                  type="email"
                  placeholder="client@example.com"
                  className="w-full border border-slate-300 px-3.5 py-2.5 text-sm bg-slate-50/40 hover:bg-white focus:bg-white focus:ring-2 focus:ring-[#002FA7]/20 focus:border-[#002FA7] outline-none transition-all placeholder:text-slate-400"
                  value={newContract.customer_email}
                  onChange={e => setNewContract({ ...newContract, customer_email: e.target.value })}
                />
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <div>
                  <label className="text-xs font-semibold text-slate-700 block mb-1.5">Product Name</label>
                  <input
                    placeholder="e.g. CCTV, DDL, VDP"
                    className="w-full border border-slate-300 px-3.5 py-2.5 text-sm bg-slate-50/40 hover:bg-white focus:bg-white focus:ring-2 focus:ring-[#002FA7]/20 focus:border-[#002FA7] outline-none transition-all placeholder:text-slate-400"
                    value={newContract.product_name}
                    onChange={e => setNewContract({ ...newContract, product_name: e.target.value })}
                  />
                </div>
                <div>
                  <label className="text-xs font-semibold text-slate-700 block mb-1.5">Area / Locality</label>
                  <input
                    placeholder="e.g. BODAKDEV, PALDI"
                    className="w-full border border-slate-300 px-3.5 py-2.5 text-sm bg-slate-50/40 hover:bg-white focus:bg-white focus:ring-2 focus:ring-[#002FA7]/20 focus:border-[#002FA7] outline-none transition-all placeholder:text-slate-400"
                    value={newContract.area}
                    onChange={e => setNewContract({ ...newContract, area: e.target.value })}
                  />
                </div>
                <div>
                  <label className="text-xs font-semibold text-slate-700 block mb-1.5">PostCode / Pincode</label>
                  <input
                    placeholder="e.g. 380058"
                    className="w-full border border-slate-300 px-3.5 py-2.5 text-sm bg-slate-50/40 hover:bg-white focus:bg-white focus:ring-2 focus:ring-[#002FA7]/20 focus:border-[#002FA7] outline-none transition-all placeholder:text-slate-400"
                    value={newContract.postcode}
                    onChange={e => setNewContract({ ...newContract, postcode: e.target.value })}
                  />
                </div>
              </div>

              <div>
                <label className="text-xs font-semibold text-slate-700 block mb-1.5">Full Site / Installation Address</label>
                <input
                  placeholder="Complete client address with floor, building, street..."
                  className="w-full border border-slate-300 px-3.5 py-2.5 text-sm bg-slate-50/40 hover:bg-white focus:bg-white focus:ring-2 focus:ring-[#002FA7]/20 focus:border-[#002FA7] outline-none transition-all placeholder:text-slate-400"
                  value={newContract.address}
                  onChange={e => setNewContract({ ...newContract, address: e.target.value })}
                />
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <div>
                  <label className="text-xs font-semibold text-slate-700 block mb-1.5">
                    Start Date <span className="text-red-500 font-bold">*</span>
                  </label>
                  <input
                    type="date"
                    required
                    className="w-full border border-slate-300 px-3.5 py-2.5 text-sm bg-slate-50/40 hover:bg-white focus:bg-white focus:ring-2 focus:ring-[#002FA7]/20 focus:border-[#002FA7] outline-none transition-all font-medium"
                    value={newContract.start_date}
                    onChange={e => setNewContract({ ...newContract, start_date: e.target.value })}
                  />
                </div>
                <div>
                  <label className="text-xs font-semibold text-slate-700 block mb-1.5">
                    Expiry Date <span className="text-red-500 font-bold">*</span>
                  </label>
                  <input
                    type="date"
                    required
                    className="w-full border border-slate-300 px-3.5 py-2.5 text-sm bg-slate-50/40 hover:bg-white focus:bg-white focus:ring-2 focus:ring-[#002FA7]/20 focus:border-[#002FA7] outline-none transition-all font-medium"
                    value={newContract.expiry_date}
                    onChange={e => setNewContract({ ...newContract, expiry_date: e.target.value })}
                  />
                </div>
                <div>
                  <label className="text-xs font-semibold text-slate-700 block mb-1.5">Amount (₹)</label>
                  <div className="relative">
                    <span className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400 font-bold text-sm">₹</span>
                    <input
                      type="number"
                      placeholder="e.g. 3000"
                      className="w-full border border-slate-300 pl-8 pr-3.5 py-2.5 text-sm bg-slate-50/40 hover:bg-white focus:bg-white focus:ring-2 focus:ring-[#002FA7]/20 focus:border-[#002FA7] outline-none transition-all placeholder:text-slate-400"
                      value={newContract.amount}
                      onChange={e => setNewContract({ ...newContract, amount: e.target.value })}
                    />
                  </div>
                </div>
              </div>

              <div>
                <label className="text-xs font-semibold text-slate-700 block mb-1.5">Remarks / Notes</label>
                <textarea
                  className="w-full border border-slate-300 px-3.5 py-2.5 text-sm bg-slate-50/40 hover:bg-white focus:bg-white focus:ring-2 focus:ring-[#002FA7]/20 focus:border-[#002FA7] outline-none transition-all placeholder:text-slate-400 resize-none"
                  rows={2}
                  value={newContract.notes}
                  onChange={e => setNewContract({ ...newContract, notes: e.target.value })}
                  placeholder="Enter any additional service terms, serial numbers, warranty conditions or remarks..."
                />
              </div>

            </div>

            {/* Modal Footer */}
            <div className="px-6 py-4 border-t border-[#E5E7EB] bg-slate-50 flex flex-col-reverse sm:flex-row items-center justify-end gap-3">
              <button
                type="button"
                onClick={() => {
                  setShowAdd(false);
                  setNewContract(initialContractState);
                }}
                className="w-full sm:w-auto border border-slate-300 px-5 py-2.5 text-sm font-bold text-slate-700 hover:bg-white hover:border-slate-400 transition-colors"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={async () => {
                  if (!newContract.card_no?.trim()) {
                    toast.error("Card No is required");
                    return;
                  }
                  if (!newContract.customer_name?.trim()) {
                    toast.error("Customer / Client Name is required");
                    return;
                  }
                  if (!newContract.customer_phone?.trim()) {
                    toast.error("Phone number / WhatsApp is required");
                    return;
                  }
                  if (!newContract.start_date?.trim()) {
                    toast.error("Start Date is required");
                    return;
                  }
                  if (!newContract.expiry_date?.trim()) {
                    toast.error("Expiry Date is required");
                    return;
                  }

                  try {
                    const payload = {
                      ...newContract,
                      amount: newContract.amount ? Number(newContract.amount) : null
                    };
                    if (!payload.assigned_to) delete payload.assigned_to;
                    await api.post("/contracts", payload);
                    toast.success("Service contract created successfully!");
                    setShowAdd(false);
                    setNewContract(initialContractState);
                    loadContracts();
                  } catch (e) {
                    toast.error(e.response?.data?.detail || "Failed to create contract");
                  }
                }}
                className="w-full sm:w-auto bg-[#002FA7] hover:bg-[#00227A] text-white px-7 py-2.5 font-bold text-sm shadow-sm transition-all"
              >
                Save Service Contract
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Import CSV Modal */}
      {showImportModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-xs animate-in fade-in duration-200">
          <div className="bg-white border border-[#D1D5DB] shadow-2xl max-w-xl w-full flex flex-col max-h-[90vh] overflow-hidden">
            {/* Modal Header */}
            <div className="px-6 py-4 border-b border-[#E5E7EB] bg-slate-50 flex items-center justify-between">
              <div>
                <div className="text-[10px] font-bold uppercase tracking-widest text-[#002FA7] flex items-center gap-1.5 mb-1">
                  <UploadSimple size={14} weight="bold" /> Bulk Data Sync
                </div>
                <h2 className="text-xl font-black text-slate-900 tracking-tight">Import Service Contracts</h2>
              </div>
              <button
                type="button"
                onClick={() => {
                  setShowImportModal(false);
                  setImportFile(null);
                  setImportResult(null);
                }}
                className="text-slate-400 hover:text-slate-700 p-1.5 rounded-sm hover:bg-slate-200/60 transition-colors"
                title="Close"
              >
                <X size={18} weight="bold" />
              </button>
            </div>

            {/* Modal Body */}
            <div className="p-6 overflow-y-auto space-y-5">
              {/* Template Download Banner */}
              <div className="bg-blue-50/60 border border-blue-200 p-3.5 flex items-center justify-between gap-3">
                <div className="flex items-center gap-2.5">
                  <FileCsv size={24} weight="duotone" className="text-[#002FA7] shrink-0" />
                  <div>
                    <div className="text-xs font-bold text-slate-900">Need the CSV Template?</div>
                    <div className="text-[11px] text-slate-600">Download the standard sample format with column headers.</div>
                  </div>
                </div>
                <button
                  type="button"
                  onClick={handleDownloadTemplate}
                  className="bg-white border border-blue-300 text-[#002FA7] hover:bg-blue-100/50 px-3 py-1.5 text-xs font-bold transition-colors flex items-center gap-1.5 shrink-0 shadow-2xs"
                >
                  <DownloadSimple size={14} weight="bold" />
                  <span>Download Sample</span>
                </button>
              </div>

              {/* Success Result Banner */}
              {importResult && (
                <div className="bg-emerald-50 border border-emerald-200 p-4 space-y-2">
                  <div className="flex items-center gap-2 text-emerald-800 font-bold text-sm">
                    <CheckCircle size={18} weight="fill" className="text-emerald-600" />
                    <span>Import Completed Successfully!</span>
                  </div>
                  <div className="text-xs text-emerald-700 pl-6 space-y-1">
                    <p>{importResult.message}</p>
                    <div className="flex items-center gap-4 pt-1 font-semibold text-slate-700 text-[11px]">
                      <span>Total Processed: <strong>{importResult.total}</strong></span>
                      <span>•</span>
                      <span>New Contracts: <strong className="text-emerald-600">+{importResult.imported}</strong></span>
                      <span>•</span>
                      <span>Updated: <strong className="text-blue-600">{importResult.updated}</strong></span>
                    </div>
                  </div>
                </div>
              )}

              {/* File Upload Drop Zone */}
              <div>
                <label className="text-xs font-bold text-slate-700 block mb-2">Upload CSV File <span className="text-red-500">*</span></label>
                <div
                  onClick={() => fileInputRef.current?.click()}
                  className={`border-2 border-dashed rounded-none p-6 text-center cursor-pointer transition-all ${importFile
                    ? "border-[#002FA7] bg-blue-50/30"
                    : "border-slate-300 hover:border-slate-400 hover:bg-slate-50/50"
                    }`}
                >
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept=".csv,text/csv"
                    className="hidden"
                    onChange={(e) => {
                      if (e.target.files && e.target.files[0]) {
                        setImportFile(e.target.files[0]);
                        setImportResult(null);
                      }
                    }}
                  />
                  {importFile ? (
                    <div className="flex flex-col items-center gap-2">
                      <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center text-[#002FA7]">
                        <FileCsv size={24} weight="bold" />
                      </div>
                      <div className="font-bold text-slate-900 text-sm">{importFile.name}</div>
                      <div className="text-xs text-slate-500">({(importFile.size / 1024).toFixed(1)} KB) • Click to change file</div>
                    </div>
                  ) : (
                    <div className="flex flex-col items-center gap-2">
                      <div className="w-10 h-10 rounded-full bg-slate-100 flex items-center justify-center text-slate-500">
                        <FileArrowUp size={24} weight="duotone" />
                      </div>
                      <div className="text-sm font-bold text-slate-800">
                        Click to select or drag and drop your CSV file
                      </div>
                      <div className="text-xs text-slate-500">Supports .csv exported from Excel, Sheets, or custom ERP</div>
                    </div>
                  )}
                </div>
              </div>

              {/* Target Service Type Option */}
              <div>
                <label className="text-xs font-bold text-slate-700 block mb-1.5">
                  Target Service Type / Categorization
                </label>
                <select
                  value={importDefaultType}
                  onChange={(e) => setImportDefaultType(e.target.value)}
                  className="w-full border border-slate-300 px-3.5 py-2.5 text-sm bg-white focus:ring-2 focus:ring-[#002FA7]/20 focus:border-[#002FA7] outline-none transition-all font-medium text-slate-800"
                >
                  <option value="auto">Auto-Detect from CSV & Expiry Dates (Recommended)</option>
                  <option value="warranty">Force Type: Warranty (Active)</option>
                  <option value="out_of_warranty">Force Type: Out of Warranty (Expired)</option>
                  <option value="amc">Force Type: AMC (Active AMC)</option>
                  <option value="out_of_amc">Force Type: Out of AMC (Expired AMC)</option>
                  <option value="on_call">Force Type: On Call</option>
                  <option value="register">Force Type: Register</option>
                  <option value="other">Force Type: Other</option>
                  {serviceTypes
                    .filter(t => !["warranty", "out_of_warranty", "amc", "out_of_amc", "on_call", "register", "other"].includes(t.value))
                    .map(t => (
                      <option key={t.value} value={t.value}>Force Type: {t.label}</option>
                    ))}
                </select>
                <p className="text-[11px] text-slate-500 mt-1">
                  When set to Auto-Detect, client types (AMC Client, Warranty, etc.) and contract dates are parsed intelligently.
                </p>
              </div>

              {/* Column Mapping Help */}
              <div className="border border-slate-200 bg-slate-50/70 p-3.5 space-y-2">
                <div className="text-[11px] font-bold uppercase tracking-wider text-slate-700 flex items-center gap-1">
                  <Check size={13} weight="bold" className="text-[#002FA7]" /> Recognized CSV Columns
                </div>
                <div className="flex flex-wrap gap-1.5 text-[11px]">
                  {["Card No", "Client Name", "WhatsApp No", "Email", "Address", "Area", "PostCode", "Client Type", "Product Name", "Contract Start Date", "Contract End Date / Expiry", "Amount"].map(col => (
                    <span key={col} className="bg-white border border-slate-200 px-2 py-0.5 font-medium text-slate-700 shadow-2xs">
                      {col}
                    </span>
                  ))}
                </div>
                <div className="text-[10px] text-slate-500 leading-relaxed">
                  ✓ Handles duplicate date headers (Start & Expiry) seamlessly.<br />
                  ✓ Automatically computes service start and expiry follow-up sequences.
                </div>
              </div>
            </div>

            {/* Modal Footer */}
            <div className="px-6 py-4 border-t border-[#E5E7EB] bg-slate-50 flex items-center justify-end gap-3">
              <button
                type="button"
                onClick={() => {
                  setShowImportModal(false);
                  setImportFile(null);
                  setImportResult(null);
                }}
                className="border border-slate-300 px-5 py-2.5 text-sm font-bold text-slate-700 hover:bg-white hover:border-slate-400 transition-colors"
              >
                {importResult ? "Close" : "Cancel"}
              </button>
              <button
                type="button"
                disabled={!importFile || importing}
                onClick={handleImportSubmit}
                className="bg-[#002FA7] hover:bg-[#00227A] disabled:opacity-50 disabled:cursor-not-allowed text-white px-7 py-2.5 font-bold text-sm shadow-sm transition-all flex items-center gap-2"
              >
                {importing ? (
                  <>
                    <ArrowsClockwise size={16} weight="bold" className="animate-spin" />
                    <span>Importing Contracts...</span>
                  </>
                ) : (
                  <>
                    <UploadSimple size={16} weight="bold" />
                    <span>Upload & Process CSV</span>
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

